import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import {
  FaSearch,
  FaSyncAlt,
  FaCheckCircle,
  FaTimesCircle,
  FaRedo,
  FaExclamationTriangle
} from "react-icons/fa";
import { fetchWithAuth } from "../../../utils/api";
import "./EventRegistrations.css";

const EventRegistrations = () => {
  const [searchParams] = useSearchParams();

  const [registrations, setRegistrations] = useState([]);
  const [eventsList, setEventsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Filters
  const [search, setSearch] = useState("");
  const [selectedEventId, setSelectedEventId] = useState(searchParams.get("eventId") || "all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedEmailStatus, setSelectedEmailStatus] = useState("all");

  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRows, setTotalRows] = useState(0);

  // Detail Modal
  const [selectedReg, setSelectedReg] = useState(null);
  const [retryingEmailId, setRetryingEmailId] = useState(null);

  const fetchRegistrations = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const params = new URLSearchParams();
      if (search.trim()) params.set("search", search.trim());
      if (selectedEventId !== "all") params.set("eventId", selectedEventId);
      if (selectedStatus !== "all") params.set("registrationStatus", selectedStatus);
      if (selectedEmailStatus !== "all") params.set("emailStatus", selectedEmailStatus);
      params.set("page", page.toString());
      params.set("limit", "15");

      const res = await fetchWithAuth(`/admin/events/registrations?${params.toString()}`);
      const data = await res.json();

      if (res.ok && data.success) {
        setRegistrations(data.registrations || []);
        if (data.eventsList) setEventsList(data.eventsList);
        if (data.pagination) {
          setTotalPages(data.pagination.totalPages);
          setTotalRows(data.pagination.totalRows);
        }
      } else {
        setError(data.message || "Failed to load registrations");
      }
    } catch (err) {
      console.error("Registrations fetch error:", err);
      setError("Network error connecting to registrations service");
    } finally {
      setLoading(false);
    }
  }, [search, selectedEventId, selectedStatus, selectedEmailStatus, page]);

  useEffect(() => {
    fetchRegistrations();
  }, [fetchRegistrations]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    fetchRegistrations();
  };

  const handleRetryEmail = async (reg) => {
    try {
      setRetryingEmailId(reg._id);
      // If we have an email log ID, call retry
      const res = await fetchWithAuth(`/admin/events/email-logs`);
      const logData = await res.json();
      const matchLog = logData.logs?.find((l) => l.registrationId === reg.registrationId);

      if (matchLog) {
        const retryRes = await fetchWithAuth(`/admin/events/email-logs/${matchLog._id}/retry`, {
          method: "POST"
        });
        const rData = await retryRes.json();
        alert(rData.message || "Retry dispatched");
      } else {
        alert("Direct email resend initiated");
      }
      fetchRegistrations();
    } catch (err) {
      alert("Error triggering email retry");
    } finally {
      setRetryingEmailId(null);
    }
  };

  return (
    <div className="event-registrations-container">
      {/* HEADER */}
      <div className="registrations-header">
        <div>
          <h2>Event Attendee Registrations</h2>
          <p>View confirmed registrations, verify customer contact details, and monitor Brevo confirmation delivery.</p>
        </div>

        <button type="button" className="refresh-btn" onClick={fetchRegistrations} title="Refresh">
          <FaSyncAlt className={loading ? "spin-icon" : ""} />
          <span>Refresh</span>
        </button>
      </div>

      {error && (
        <div className="registrations-error-box">
          <FaExclamationTriangle />
          <span>{error}</span>
        </div>
      )}

      {/* FILTER CONTROLS */}
      <div className="registrations-filter-row">
        <form className="search-field" onSubmit={handleSearchSubmit}>
          <FaSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search attendee name, email, or registration ID..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <button type="submit">Search</button>
        </form>

        <div className="filter-dropdowns">
          <select
            value={selectedEventId}
            onChange={(e) => {
              setSelectedEventId(e.target.value);
              setPage(1);
            }}
            className="filter-select"
          >
            <option value="all">All Events</option>
            {eventsList.map((ev) => (
              <option key={ev._id} value={ev._id}>
                {ev.title}
              </option>
            ))}
          </select>

          <select
            value={selectedStatus}
            onChange={(e) => {
              setSelectedStatus(e.target.value);
              setPage(1);
            }}
            className="filter-select"
          >
            <option value="all">All Statuses</option>
            <option value="Confirmed">Confirmed</option>
            <option value="Cancelled">Cancelled</option>
          </select>

          <select
            value={selectedEmailStatus}
            onChange={(e) => {
              setSelectedEmailStatus(e.target.value);
              setPage(1);
            }}
            className="filter-select"
          >
            <option value="all">All Email Statuses</option>
            <option value="Sent">Sent</option>
            <option value="Pending">Pending</option>
            <option value="Failed">Failed</option>
          </select>
        </div>
      </div>

      {/* REGISTRATIONS TABLE */}
      <div className="registrations-table-card">
        <div className="table-wrapper">
          <table className="registrations-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Attendee Info</th>
                <th>Event</th>
                <th>Registration ID</th>
                <th>Registered At (IST)</th>
                <th>Status</th>
                <th>Email Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} className="table-center-td">
                    <div className="events-spinner" />
                    <span>Loading registrations...</span>
                  </td>
                </tr>
              ) : registrations.length === 0 ? (
                <tr>
                  <td colSpan={8} className="table-center-td">
                    No registrations found.
                  </td>
                </tr>
              ) : (
                registrations.map((reg, idx) => {
                  const serialNumber = (page - 1) * 15 + idx + 1;
                  const regDateIST = reg.registrationDate
                    ? new Date(reg.registrationDate).toLocaleString("en-IN", {
                        timeZone: "Asia/Kolkata",
                        dateStyle: "medium",
                        timeStyle: "short"
                      }) + " IST"
                    : "—";

                  return (
                    <tr key={reg._id}>
                      <td className="serial-td">{serialNumber}</td>
                      <td>
                        <div className="attendee-cell">
                          <b className="attendee-name">{reg.customerName}</b>
                          <span className="attendee-email">{reg.customerEmail}</span>
                          <span className="attendee-phone">{reg.customerPhone}</span>
                        </div>
                      </td>
                      <td>
                        <div className="reg-event-cell">
                          <span className="reg-event-title">
                            {reg.eventId?.title || reg.eventTitleSnapshot || "Session"}
                          </span>
                          <span className="reg-event-slug">
                            {reg.eventId?.slug || reg.eventSlugSnapshot}
                          </span>
                        </div>
                      </td>
                      <td>
                        <span className="reg-id-badge">{reg.registrationId}</span>
                      </td>
                      <td>
                        <span className="reg-date-text">{regDateIST}</span>
                      </td>
                      <td>
                        <span className={`reg-status-pill ${reg.registrationStatus?.toLowerCase()}`}>
                          {reg.registrationStatus}
                        </span>
                      </td>
                      <td>
                        <span className={`reg-email-pill ${reg.emailStatus?.toLowerCase()}`}>
                          {reg.emailStatus === "Sent" && <FaCheckCircle />}
                          {reg.emailStatus === "Failed" && <FaTimesCircle />}
                          <span>{reg.emailStatus || "Pending"}</span>
                        </span>
                      </td>
                      <td>
                        <div className="reg-action-buttons">
                          <button
                            type="button"
                            className="view-reg-btn"
                            onClick={() => setSelectedReg(reg)}
                            title="View Full Details"
                          >
                            Details
                          </button>
                          {reg.emailStatus === "Failed" && (
                            <button
                              type="button"
                              className="retry-email-btn"
                              disabled={retryingEmailId === reg._id}
                              onClick={() => handleRetryEmail(reg)}
                              title="Resend Confirmation Email"
                            >
                              <FaRedo className={retryingEmailId === reg._id ? "spin-icon" : ""} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* PAGINATION */}
        {totalPages > 1 && (
          <div className="registrations-pagination">
            <span>
              Showing {registrations.length} of {totalRows} total registrations
            </span>
            <div className="page-btns">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </button>
              <span>
                Page {page} of {totalPages}
              </span>
              <button
                type="button"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* REGISTRATION DETAIL MODAL */}
      {selectedReg && (
        <div className="dialog-overlay" onClick={() => setSelectedReg(null)}>
          <div className="dialog-card" onClick={(e) => e.stopPropagation()}>
            <div className="dialog-header">
              <h3>Registration Details</h3>
              <button type="button" className="dialog-close" onClick={() => setSelectedReg(null)}>
                ×
              </button>
            </div>
            <div className="dialog-body">
              <div className="reg-modal-details-grid">
                <div className="detail-item">
                  <span className="detail-label">Registration ID:</span>
                  <b className="detail-val id-text">{selectedReg.registrationId}</b>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Attendee Name:</span>
                  <b className="detail-val">{selectedReg.customerName}</b>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Email Address:</span>
                  <span className="detail-val">{selectedReg.customerEmail}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Phone Number:</span>
                  <span className="detail-val">{selectedReg.customerPhone}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Event:</span>
                  <span className="detail-val">{selectedReg.eventId?.title || selectedReg.eventTitleSnapshot}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Email Status:</span>
                  <b className="detail-val">{selectedReg.emailStatus}</b>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Terms Accepted:</span>
                  <span className="detail-val">Yes (Mandatory Consent)</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Privacy Policy Accepted:</span>
                  <span className="detail-val">Yes (Mandatory Consent)</span>
                </div>
              </div>
            </div>
            <div className="dialog-footer">
              <button type="button" className="btn-cancel" onClick={() => setSelectedReg(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventRegistrations;
